import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

type AnalyticsConsentChoice = 'accepted' | 'declined';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const CONSENT_STORAGE_KEY = 'perfectodds_analytics_consent_v1';
const COOKIE_SETTINGS_EVENT = 'perfectodds:open-cookie-settings';
const GOOGLE_TAG_SCRIPT_ID = 'perfectodds-google-analytics';
const MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const ANALYTICS_ENABLED = process.env.NODE_ENV === 'production' && /^G-[A-Z0-9]+$/i.test(MEASUREMENT_ID);

let consentDefaultsInitialized = false;
let analyticsInitialized = false;
let lastTrackedLocation = '';

const getStoredConsent = (): AnalyticsConsentChoice | null => {
  try {
    const storedChoice = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return storedChoice === 'accepted' || storedChoice === 'declined' ? storedChoice : null;
  } catch {
    return null;
  }
};

const storeConsent = (choice: AnalyticsConsentChoice) => {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // The preference remains active for this page load when browser storage is unavailable.
  }
};

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(..._args: unknown[]) {
    window.dataLayer?.push(arguments);
  };
};

const setAnalyticsDisabled = (disabled: boolean) => {
  (window as unknown as Record<string, unknown>)[`ga-disable-${MEASUREMENT_ID}`] = disabled;
};

const initializeConsentDefaults = () => {
  if (consentDefaultsInitialized || !ANALYTICS_ENABLED) return;
  consentDefaultsInitialized = true;
  ensureGtag();
  window.gtag?.('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
};

const enableAnalytics = () => {
  if (!ANALYTICS_ENABLED) return;
  initializeConsentDefaults();
  setAnalyticsDisabled(false);
  window.gtag?.('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  if (analyticsInitialized) return;
  analyticsInitialized = true;
  window.gtag?.('js', new Date());
  window.gtag?.('config', MEASUREMENT_ID, {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    send_page_view: false,
  });

  if (!document.getElementById(GOOGLE_TAG_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GOOGLE_TAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  }
};

const clearAnalyticsCookies = () => {
  const analyticsCookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name?.startsWith('_ga')));
  const hostname = window.location.hostname;
  const rootDomain = hostname.split('.').slice(-2).join('.');
  const domains = Array.from(new Set([hostname, `.${hostname}`, rootDomain, `.${rootDomain}`]));

  analyticsCookieNames.forEach((name) => {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`;
    });
  });
};

const disableAnalytics = () => {
  if (!ANALYTICS_ENABLED) return;
  initializeConsentDefaults();
  lastTrackedLocation = '';
  setAnalyticsDisabled(true);
  window.gtag?.('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  clearAnalyticsCookies();
};

export const openCookiePreferences = () => {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
};

export const AnalyticsConsent: React.FC = () => {
  const location = useLocation();
  const [choice, setChoice] = useState<AnalyticsConsentChoice | null>(() => (
    ANALYTICS_ENABLED ? getStoredConsent() : null
  ));
  const [isOpen, setIsOpen] = useState(() => ANALYTICS_ENABLED && getStoredConsent() === null);

  useEffect(() => {
    if (!ANALYTICS_ENABLED) return;
    initializeConsentDefaults();
    if (choice === 'accepted') {
      enableAnalytics();
    } else if (choice === 'declined') {
      disableAnalytics();
    }
  }, [choice]);

  useEffect(() => {
    if (!ANALYTICS_ENABLED || choice !== 'accepted') return;
    const pageLocation = `${window.location.origin}${location.pathname}${location.search}`;
    if (pageLocation === lastTrackedLocation) return;

    const trackPageView = window.setTimeout(() => {
      if (getStoredConsent() !== 'accepted' || pageLocation === lastTrackedLocation) return;
      lastTrackedLocation = pageLocation;
      window.gtag?.('event', 'page_view', {
        page_location: pageLocation,
        page_title: document.title,
      });
    }, 0);

    return () => window.clearTimeout(trackPageView);
  }, [choice, location.pathname, location.search]);

  useEffect(() => {
    if (!ANALYTICS_ENABLED) return;
    const handleOpenSettings = () => setIsOpen(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, handleOpenSettings);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, handleOpenSettings);
  }, []);

  if (!ANALYTICS_ENABLED || !isOpen) return null;

  const selectChoice = (nextChoice: AnalyticsConsentChoice) => {
    storeConsent(nextChoice);
    setChoice(nextChoice);
    setIsOpen(false);
  };

  return (
    <aside className="cookie-consent" role="dialog" aria-labelledby="cookie-consent-title" aria-describedby="cookie-consent-description">
      <div className="cookie-consent-copy">
        <span className="cookie-consent-kicker">Jouw privacy</span>
        <h2 id="cookie-consent-title">Mogen we cookies gebruiken?</h2>
        <p id="cookie-consent-description">
          Met optionele cookies zien we welke pagina&apos;s goed werken en waar de site beter kan. We plaatsen deze
          cookies alleen na jouw toestemming. Je keuze kun je later via de footer aanpassen.
        </p>
        <Link to="/privacybeleid">Lees het privacybeleid</Link>
      </div>
      <div className="cookie-consent-actions">
        <button type="button" className="cookie-consent-decline" onClick={() => selectChoice('declined')}>
          Alleen noodzakelijke cookies
        </button>
        <button type="button" className="cookie-consent-accept" onClick={() => selectChoice('accepted')}>
          Cookies toestaan
        </button>
      </div>
    </aside>
  );
};
